import { Button, ButtonGroup, IconButton, useTheme } from '@mui/material';
import React from 'react';
import { useNotify } from 'src/lib/notify';
import { SirenaViewer, useLegacyMermaidRender } from 'src/lib/sirena';
import { cn, styleVars } from 'src/utils/styles';
import { match } from 'ts-pattern';
import TablerIconCode from '~icons/tabler/code';
import TablerIconCopy from '~icons/tabler/copy';
import TablerIconEye from '~icons/tabler/eye';
import ChatCodeBlock from './chat-codeblock';

export interface ChatDiagramProps extends React.PropsWithChildren {}

function ChatGeneratedDiagram({ children }: ChatDiagramProps) {
  const notify = useNotify();
  const [tab, setTab] = React.useState<'code' | 'diagram'>('code');
  const mermaidRender = useLegacyMermaidRender();
  const code = React.useMemo(() => String(children), [children]);
  const theme = useTheme();

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore as we only want to update for `tab` or `code` changes
  React.useEffect(() => {
    if (tab === 'diagram') {
      mermaidRender.update(code);
    }
  }, [tab, code]);

  function handleCopy() {
    navigator.clipboard.writeText(`\`\`\`mermaid\n${code}\`\`\``);
    notify.send({
      message: 'Copied to clipboard',
      severity: 'success',
    });
  }

  return (
    <div
      className={cn('relative pt-4 rounded-md overflow-hidden', {
        'bg-[#2F2F2F]': tab === 'code',
        'bg-background-paper': tab === 'diagram',
      })}
    >
      <div
        className='absolute top-2 left-2 z-(--chat-generated-diagram-z)'
        style={styleVars({
          '--chat-generated-diagram-z': (theme.zIndex.drawer - 1).toString(),
        })}
      >
        <ButtonGroup variant='outlined' size='small'>
          <Button
            onClick={() => setTab('code')}
            variant={tab === 'code' ? 'contained' : 'outlined'}
          >
            <TablerIconCode />
          </Button>
          <Button
            onClick={() => setTab('diagram')}
            variant={tab === 'diagram' ? 'contained' : 'outlined'}
          >
            <TablerIconEye />
          </Button>
        </ButtonGroup>
      </div>
      <div className='absolute top-2 right-2 z-10'>
        <IconButton size='small' onClick={handleCopy}>
          <TablerIconCopy />
        </IconButton>
      </div>
      {match(tab)
        .with('code', () => (
          <ChatCodeBlock
            code={code}
            language='mermaid'
            className='relative z-0'
          />
        ))
        .with('diagram', () => (
          <SirenaViewer
            graphType={mermaidRender.graphType}
            svg={mermaidRender.graphSvg}
            error={mermaidRender.error}
            loading={
              (mermaidRender.status === 'loading' || !mermaidRender.graphSvg) &&
              !mermaidRender.error
            }
            sx={{
              position: 'unset',
            }}
          />
        ))
        .exhaustive()}
    </div>
  );
}

export default ChatGeneratedDiagram;
